import { Component } from 'react'
import {
  IdentifierReels,
  ItemIdentifiers,
  Products,
  SimpleReadingListBatchValidateResEntry,
  TmrStatus
} from 'stylewhere/api'
import {
  RoundedLabel,
  StatusChip,
  Box,
  IdentifierInfoModal,
  ProductInfoModal,
  ReelInfoModal
} from 'stylewhere/components'
import styled from '@emotion/styled'
import { SimpleReadingListOperationConfig } from 'stylewhere/shared'
import { closeModal, isModalError, openModal, showToastError } from 'stylewhere/shared/utils'
import { __, T } from 'stylewhere/shared/i18n'

interface Props {
  row: SimpleReadingListBatchValidateResEntry
  operation: SimpleReadingListOperationConfig
  hasChecklist?: boolean
  showCounters?: boolean
  targetEntity?: 'NONE' | 'identifiers' | 'identifierReels' | 'items' | 'products'
}

export class EntryRow extends Component<Props> {
  isModal = false

  componentDidMount(): void {
    const { operation } = this.props
    this.isModal = isModalError(operation)
  }

  onPressIdentifier = async (entry) => {
    try {
      const idx = (await ItemIdentifiers.search({ equalCodes: entry.data.code, size: 1 })).content
      if (idx.length === 0) throw new Error(__(T.error.tag_not_found))
      openModal({
        id: 'identifier-modal',
        modal: <IdentifierInfoModal identifier={idx[0]} onClose={() => closeModal('identifier-modal')} visible />
      })
    } catch (error: any) {
      showToastError(error && error.message ? error.message : 'Unknown error', __(T.error.error), this.isModal)
    }
  }

  onPressReel = async (entry) => {
    try {
      const reel = (await IdentifierReels.search({ equalCodes: entry.data.code, size: 1 })).content
      if (reel.length === 0) throw new Error(__(T.error.tag_not_found))
      openModal({
        id: 'reel-modal',
        modal: <ReelInfoModal reel={reel[0]} onClose={() => closeModal('reel-modal')} visible />
      })
    } catch (error: any) {
      showToastError(error && error.message ? error.message : 'Unknown error', __(T.error.error), this.isModal)
    }
  }

  onPressProduct = async (entry) => {
    try {
      const product = (await Products.searchCodes([entry.data.code])).content
      if (product.length === 0) throw new Error(__(T.error.product_not_found))
      openModal({
        id: 'product-modal',
        modal: <ProductInfoModal product={product[0]} onClose={() => closeModal('product-modal')} visible />
      })
    } catch (error: any) {
      showToastError(error && error.message ? error.message : 'Unknown error', __(T.error.error), this.isModal)
    }
  }

  onPress = (entry) => {
    console.log('press')
  }

  _renderValue = (read?: number, expected?: number) => {
    if (read === undefined) return undefined
    const { hasChecklist } = this.props

    const displayText = expected !== undefined ? `${read}/${expected}` : `${read}`
    return (
      <Box center ml={15}>
        <SmallCounter
          data-testid={`small-counter`}
          mr={0}
          ph={20}
          detected={read}
          expected={expected}
          unexpected={undefined}
          noChecklist={!hasChecklist}
          status={undefined}
        >
          {displayText}
        </SmallCounter>
      </Box>
    )
  }

  sortStatusChip = (status: string) => {
    const { operation } = this.props
    const chipOrder = {
      error: -1,
      warning: 0,
      default: 1
    }
    let statusType = chipOrder.default
    if (operation?.itemStatuses.warning.includes(status)) statusType = chipOrder.warning
    if (operation?.itemStatuses.error.includes(status)) statusType = chipOrder.error

    return statusType
  }

  _renderCodeAndStatus = (data: SimpleReadingListBatchValidateResEntry) => {
    const { operation } = this.props
    const statuses = data.statuses
    return (
      <Box flex justify={'center'} mr={0} ml={15}>
        <Box row style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
          <RoundedLabel value={data.data.code} style={{ backgroundColor: '#333', color: '#FFF' }} />
        </Box>
        {statuses && statuses.length > 0 && (
          <Box row flexWrap>
            {statuses.sort(this.sortStatusChip).map((status, index) => (
              <StatusChip key={index} status={status} operation={operation} index={index} />
            ))}
          </Box>
        )}
      </Box>
    )
  }

  render() {
    const { row, showCounters, targetEntity } = this.props
    const value = showCounters ? this._renderValue(row.detected, row.expected) : undefined
    switch (targetEntity) {
      case 'NONE':
        return (
          <Row data-testid={`entry-row`} row p={15}>
            {this._renderCodeAndStatus(row)}
            {value}
          </Row>
        )
      case 'identifiers':
        return (
          <Row data-testid={`entry-row`} row onClick={() => this.onPressIdentifier(row)} p={15}>
            {this._renderCodeAndStatus(row)}
            {value}
          </Row>
        )
      case 'identifierReels':
        return (
          <Row data-testid={`entry-row`} row onClick={() => this.onPressReel(row)} p={15}>
            {this._renderCodeAndStatus(row)}
            {value}
          </Row>
        )
      case 'products': // creare un componente adhoc?
        return (
          <Row data-testid={`entry-row`} row onClick={() => this.onPressProduct(row)} p={15}>
            {this._renderCodeAndStatus(row)}
            {value}
          </Row>
        )
      case 'items':
        // TODO come gestire gli item?
        return (
          <Row data-testid={`entry-row`} row onClick={() => this.onPress(row)} p={15}>
            {this._renderCodeAndStatus(row)}
            {value}
          </Row>
        )
      default:
        return (
          <Row data-testid={`entry-row`} row p={15}>
            {this._renderCodeAndStatus(row)}
            {value}
          </Row>
        )
    }
  }
}

const SmallCounter = styled(Box)<{
  detected?: number
  expected?: number
  unexpected?: number
  noChecklist?: boolean
  status?: TmrStatus
}>`
  font-size: 50px;
  font-weight: bold;
  text-align: center;
  min-width: 100px;
  width: 100%;
  align-self: center;
  background-color: ${({ detected = 0, expected = 0, unexpected = 0, noChecklist = false, status }) => {
    if (noChecklist) return '#F0F0F0'
    switch (status) {
      case 'error':
        return '#FF8474'
      case 'warning':
        return '#FFEA67'
      case 'ignored':
        return '#DAE6ED'
      default:
        if (detected + unexpected > expected) return '#F2B57D'
        if (noChecklist) return '#75EBA8'
        if (unexpected > 0) return '#F2B57D'
        if (detected < expected) return '#EDEDED'
        if (detected === expected) return '#75EBA8'
        return '#F0F0F0'
    }
  }};
  border-radius: 10px;
`
const Row = styled(Box)<{ mb? }>`
  margin-bottom: ${({ mb }) => (mb ? mb + 'px' : '20px')};
  border: 2px solid #edecec;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background-color: ${({ theme }) => theme.background2};
`
